import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select, { SingleValue } from 'react-select';
import makeAnimated from 'react-select/animated';

const animatedComponents = makeAnimated();

type DateParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
};

type MonthCase = 'nominative' | 'genitive';

interface DateTimePickerProps {
  label?: string;
  placeholder?: string;
  value: string | null;
  onChange: (value: string | null) => void;
  className?: string;
  inputClassName?: string;
  minISO?: string | null;
  maxISO?: string | null;
  monthCase?: MonthCase;
}

const MONTHS_RU = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];
const MONTHS_RU_GENITIVE = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

const WEEKDAYS_RU = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const pad = (n: number) => String(n).padStart(2, '0');
const sameYMD = (a: DateParts, b: DateParts) =>
  a.year === b.year && a.month === b.month && a.day === b.day;

const nowPartsMSK = (): DateParts => {
  const mskNow = new Date(Date.now() + 3 * 3600 * 1000);
  return {
    year: mskNow.getUTCFullYear(),
    month: mskNow.getUTCMonth() + 1,
    day: mskNow.getUTCDate(),
    hour: mskNow.getUTCHours(),
    minute: mskNow.getUTCMinutes(),
  };
};
const toIsoMsk = (p: DateParts): string =>
  `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}:00+03:00`;
const fromIsoToParts = (iso: string): DateParts | null => {
  const m = iso.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2})?(?:([+-]\d{2}:\d{2})|Z)?$/
  );
  if (!m) return null;
  return {
    year: parseInt(m[1], 10),
    month: parseInt(m[2], 10),
    day: parseInt(m[3], 10),
    hour: parseInt(m[4], 10),
    minute: parseInt(m[5], 10),
  };
};
const partsToMs = (p: DateParts) => new Date(toIsoMsk(p)).getTime();
const formatDisplay = (p: DateParts | null) => (!p ? '' :
  `${pad(p.day)}.${pad(p.month)}.${p.year} ${pad(p.hour)}:${pad(p.minute)} МСК`
);
const buildCalendarMatrix = (year: number, month1to12: number): { date: DateParts; inCurrentMonth: boolean }[][] => {
  const firstDay = new Date(year, month1to12 - 1, 1);
  const weekday = (firstDay.getDay() + 6) % 7;
  const startDate = new Date(year, month1to12 - 1, 1 - weekday);

  const matrix: { date: DateParts; inCurrentMonth: boolean }[][] = [];
  for (let w = 0; w < 6; w++) {
    const row: { date: DateParts; inCurrentMonth: boolean }[] = [];
    for (let d = 0; d < 7; d++) {
      const cur = new Date(startDate);
      cur.setDate(startDate.getDate() + w * 7 + d);
      const p: DateParts = {
        year: cur.getFullYear(),
        month: cur.getMonth() + 1,
        day: cur.getDate(),
        hour: 0,
        minute: 0,
      };
      row.push({ date: p, inCurrentMonth: p.month === month1to12 });
    }
    matrix.push(row);
  }
  return matrix;
};

const useOnClickOutside = (ref: React.RefObject<HTMLElement>, handler: () => void, active: boolean) => {
  useEffect(() => {
    if (!active) return;
    const listener = (e: MouseEvent | TouchEvent) => {
      const el = ref.current;
      if (!el || el.contains(e.target as Node)) return;
      handler();
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler, active]);
};

type OptionNum = { value: number; label: string; isDisabled?: boolean };

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: '40px',
    borderColor: state.isFocused ? '#60a5fa' : '#d1d5db',
    borderRadius: '0.5rem',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.2)' : '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    '&:hover': { borderColor: state.isFocused ? '#60a5fa' : '#9ca3af' },
    cursor: 'pointer',
    backgroundColor: '#fff',
  }),
  container: (base: any) => ({
    ...base,
    width: '100%',
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: '6px 10px',
    display: 'flex',
    alignItems: 'center',
  }),
  input: (base: any) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  singleValue: (base: any) => ({
    ...base,
    maxWidth: '100%',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    fontSize: '0.875rem',
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    padding: '6px 6px',
  }),
  placeholder: (base: any) => ({
    ...base,
    fontSize: '0.875rem',
    color: '#9ca3af',
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: '0.875rem',
    backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#eff6ff' : undefined,
    color: state.isSelected ? '#fff' : '#111827',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    opacity: state.isDisabled ? 0.5 : 1,
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: 9999,
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: 9999,
  }),
};

const selectStylesNoEllipsis = {
  ...selectStyles,
  singleValue: (base: any) => ({
    ...base,
    fontSize: '0.875rem',
    overflow: 'visible',
    textOverflow: 'clip',
    whiteSpace: 'nowrap',
    maxWidth: 'none',
  }),
};

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  label,
  placeholder = 'дд.мм.гггг чч:мм',
  value,
  onChange,
  className = '',
  inputClassName = '',
  minISO = null,
  maxISO = null,
  monthCase = 'genitive',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [stage, setStage] = useState<'date' | 'time'>('date');

  const initial = value ? fromIsoToParts(value) : null;
  const [tempParts, setTempParts] = useState<DateParts>(initial ?? { ...nowPartsMSK(), hour: 12, minute: 0 });

  const [viewYear, setViewYear] = useState<number>(tempParts.year);
  const [viewMonth, setViewMonth] = useState<number>(tempParts.month);

  const today = useMemo<DateParts>(() => nowPartsMSK(), []);
  const calendar = useMemo(() => buildCalendarMatrix(viewYear, viewMonth), [viewYear, viewMonth]);

  const minMs = useMemo(() => (minISO ? new Date(minISO).getTime() : null), [minISO]);
  const maxMs = useMemo(() => (maxISO ? new Date(maxISO).getTime() : null), [maxISO]);
  const minParts = useMemo(() => (minISO ? fromIsoToParts(minISO) : null), [minISO]);
  const maxParts = useMemo(() => (maxISO ? fromIsoToParts(maxISO) : null), [maxISO]);

  const containerRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(containerRef, () => setIsOpen(false), isOpen);

  useEffect(() => {
    if (isOpen) return;
    const p = value ? fromIsoToParts(value) : null;
    if (p) {
      setTempParts(p);
      setViewYear(p.year);
      setViewMonth(p.month);
    } else {
      const n = nowPartsMSK();
      setTempParts({ ...n, hour: 12, minute: 0 });
      setViewYear(n.year);
      setViewMonth(n.month);
    }
  }, [value, isOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  const clampToBounds = (p: DateParts): DateParts => {
    const ms = partsToMs(p);
    if (minMs !== null && ms < minMs && minParts) return { ...minParts };
    if (maxMs !== null && ms > maxMs && maxParts) return { ...maxParts };
    return p;
  };

  useEffect(() => {
    setTempParts(prev => clampToBounds(prev));
  }, [minMs, maxMs]);

  const displayValue = useMemo(() => {
    return value ? formatDisplay(fromIsoToParts(value)) : '';
  }, [value]);

  const open = () => {
    if (!isOpen) {
      const p = value ? fromIsoToParts(value) : { ...nowPartsMSK(), hour: 12, minute: 0 };
      const clamped = clampToBounds(p!);
      setTempParts(clamped);
      setViewYear((clamped ?? today).year);
      setViewMonth((clamped ?? today).month);
      setStage('date');
      setIsOpen(true);
    }
  };

  const handleMonthChange = (delta: number) => {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setViewMonth(m);
    setViewYear(y);
  };

  const isDayDisabled = (date: DateParts) => {
    const startMs = partsToMs({ ...date, hour: 0, minute: 0 });
    const endMs = partsToMs({ ...date, hour: 23, minute: 59 });
    if (minMs !== null && endMs < minMs) return true;
    if (maxMs !== null && startMs > maxMs) return true;
    return false;
  };

  const handleDayClick = (p: DateParts) => {
    if (isDayDisabled(p)) return;
    setViewYear(p.year);
    setViewMonth(p.month);
    const next: DateParts = clampToBounds({
      ...tempParts,
      year: p.year,
      month: p.month,
      day: p.day,
    });
    setTempParts(next);
    setStage('time');
  };

  const computeHourDisabled = (h: number) => {
    let minHour = 0;
    let maxHour = 23;
    if (minParts && sameYMD(tempParts, minParts)) minHour = minParts.hour;
    if (maxParts && sameYMD(tempParts, maxParts)) maxHour = Math.min(maxHour, maxParts.hour);
    return h < minHour || h > maxHour;
  };
  const computeMinuteDisabled = (m: number) => {
    let minMinute = 0;
    let maxMinute = 59;
    if (minParts && sameYMD(tempParts, minParts) && tempParts.hour === minParts.hour) {
      minMinute = minParts.minute;
    }
    if (maxParts && sameYMD(tempParts, maxParts) && tempParts.hour === maxParts.hour) {
      maxMinute = Math.min(maxMinute, maxParts.minute);
    }
    return m < minMinute || m > maxMinute;
  };

  const ensureTimeWithinBounds = (p: DateParts): DateParts => {
    let next = { ...p };
    for (let tries = 0; tries < 2; tries++) {
      if (computeHourDisabled(next.hour)) {
        let minHour = 0, maxHour = 23;
        if (minParts && sameYMD(next, minParts)) minHour = minParts.hour;
        if (maxParts && sameYMD(next, maxParts)) maxHour = Math.min(maxHour, maxParts.hour);
        next.hour = Math.min(Math.max(next.hour, minHour), maxHour);
      }
      if (computeMinuteDisabled(next.minute)) {
        let minMinute = 0, maxMinute = 59;
        if (minParts && sameYMD(next, minParts) && next.hour === minParts.hour) minMinute = minParts.minute;
        if (maxParts && sameYMD(next, maxParts) && next.hour === maxParts.hour) maxMinute = Math.min(maxMinute, maxParts.minute);
        next.minute = Math.min(Math.max(next.minute, minMinute), maxMinute);
      }
    }
    return next;
  };

  const apply = () => {
    const normalized = clampToBounds(ensureTimeWithinBounds(tempParts));
    onChange(toIsoMsk(normalized));
    setIsOpen(false);
  };

  const clear = () => {
    onChange(null);
    setIsOpen(false);
  };

  const validSelection = useMemo(() => {
    const ms = partsToMs(tempParts);
    if (minMs !== null && ms < minMs) return false;
    if (maxMs !== null && ms > maxMs) return false;
    return true;
  }, [tempParts, minMs, maxMs]);

  const monthOptions: OptionNum[] = useMemo(() =>
    MONTHS_RU.map((m, i) => ({ value: i + 1, label: m })), []);
  const yearOptions: OptionNum[] = useMemo(() => {
    const years: OptionNum[] = [];
    for (let y = today.year - 50; y <= today.year + 50; y++) {
      years.push({ value: y, label: String(y) });
    }
    return years;
  }, [today.year]);
  const hourOptions: OptionNum[] = useMemo(() =>
    Array.from({ length: 24 }, (_, h) => ({ value: h, label: pad(h), isDisabled: computeHourDisabled(h) })),
    [tempParts.year, tempParts.month, tempParts.day, minISO, maxISO]
  );
  const minuteOptions: OptionNum[] = useMemo(() =>
    Array.from({ length: 60 }, (_, m) => ({ value: m, label: pad(m), isDisabled: computeMinuteDisabled(m) })),
    [tempParts.year, tempParts.month, tempParts.day, tempParts.hour, minISO, maxISO]
  );

  const monthLabelForChip =
    monthCase === 'genitive'
      ? MONTHS_RU_GENITIVE[tempParts.month - 1]
      : MONTHS_RU[tempParts.month - 1].toLowerCase();

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label className="block mb-1 text-sm font-medium text-gray-700">{label}</label>
      )}

      <div className="relative" ref={containerRef}>
        <button
          type="button"
          onClick={open}
          className={`w-full flex items-center justify-between border rounded-md px-3 py-2 text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputClassName}`}
        >
          <span className={`text-gray-900 ${displayValue ? '' : 'text-gray-400'}`}>
            {displayValue || placeholder}
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-5 h-5 text-gray-500 ml-2 shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
          </svg>
        </button>

        {isOpen && (
          <div className="absolute z-50 mt-2 w-[380px] max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-lg shadow-lg">
            {/* Header — показываем только на этапе выбора даты */}
            {stage === 'date' && (
              <div className="p-3 border-b flex items-center justify-center gap-2">
                <button
                  type="button"
                  className="p-1 rounded hover:bg-gray-100"
                  onClick={() => handleMonthChange(-1)}
                  aria-label="Предыдущий месяц"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" stroke="currentColor" fill="none">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <div className="flex-none min-w-[140px]">
                  <Select<OptionNum, false>
                    components={animatedComponents}
                    className="text-sm"
                    classNamePrefix="react-select"
                    styles={selectStylesNoEllipsis}
                    isSearchable={false}
                    options={monthOptions}
                    value={monthOptions.find(o => o.value === viewMonth) || null}
                    onChange={(opt: SingleValue<OptionNum>) => opt && setViewMonth(opt.value)}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>

                <div className="w-[120px] flex-shrink-0">
                  <Select<OptionNum, false>
                    components={animatedComponents}
                    className="text-sm"
                    classNamePrefix="react-select"
                    styles={selectStylesNoEllipsis}
                    isSearchable={false}
                    options={yearOptions}
                    value={yearOptions.find(o => o.value === viewYear) || null}
                    onChange={(opt: SingleValue<OptionNum>) => opt && setViewYear(opt.value)}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>

                <button
                  type="button"
                  className="p-1 rounded hover:bg-gray-100"
                  onClick={() => handleMonthChange(1)}
                  aria-label="Следующий месяц"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" stroke="currentColor" fill="none">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}

            {/* Body */}
            <div className="p-3">
              {stage === 'date' && (
                <>
                  {/* Weekdays */}
                  <div className="grid grid-cols-7 gap-1 text-center text-xs text-gray-500 mb-1">
                    {WEEKDAYS_RU.map((d) => (
                      <div key={d} className="py-1">{d}</div>
                    ))}
                  </div>

                  {/* Days grid */}
                  <div className="grid grid-cols-7 gap-1">
                    {calendar.flat().map(({ date, inCurrentMonth }, idx) => {
                      const isToday =
                        date.year === today.year && date.month === today.month && date.day === today.day;
                      const isSelected =
                        date.year === tempParts.year && date.month === tempParts.month && date.day === tempParts.day;
                      const disabled = isDayDisabled(date);

                      let dayCls = 'py-2 text-sm rounded';
                      if (disabled) {
                        dayCls += ' text-gray-300 cursor-not-allowed opacity-50';
                      } else {
                        dayCls += inCurrentMonth ? ' text-gray-900' : ' text-gray-400';
                        if (!isSelected && !isToday) {
                          dayCls += ' hover:bg-gray-100';
                        }
                      }
                      if (isSelected && !disabled) dayCls += ' bg-blue-600 text-white';
                      if (isToday && !isSelected && !disabled) dayCls += ' ring-1 ring-blue-500';

                      return (
                        <button
                          key={idx}
                          type="button"
                          className={dayCls}
                          onClick={() => handleDayClick(date)}
                          disabled={disabled}
                        >
                          {date.day}
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex justify-end mt-3">
                    <button
                      type="button"
                      className="text-sm text-blue-600 hover:underline"
                      onClick={() => {
                        const n = nowPartsMSK();
                        setViewYear(n.year); setViewMonth(n.month);
                      }}
                    >
                      Сегодня
                    </button>
                  </div>
                </>
              )}

              {stage === 'time' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs uppercase text-gray-500 tracking-wide">Дата:</span>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 font-semibold">
                      {pad(tempParts.day)} {monthLabelForChip} {tempParts.year}
                    </span>
                  </div>

                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">Часы</label>
                      <Select<OptionNum, false>
                        components={animatedComponents}
                        className="text-sm"
                        classNamePrefix="react-select"
                        styles={selectStyles}
                        isSearchable={false}
                        options={hourOptions}
                        value={{ value: tempParts.hour, label: pad(tempParts.hour) }}
                        onChange={(opt: SingleValue<OptionNum>) => {
                          if (!opt) return;
                          const next = ensureTimeWithinBounds({ ...tempParts, hour: opt.value });
                          setTempParts(next);
                        }}
                        isOptionDisabled={(o) => !!o.isDisabled}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </div>

                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">Минуты</label>
                      <Select<OptionNum, false>
                        components={animatedComponents}
                        className="text-sm"
                        classNamePrefix="react-select"
                        styles={selectStyles}
                        isSearchable={false}
                        options={minuteOptions}
                        value={{ value: tempParts.minute, label: pad(tempParts.minute) }}
                        onChange={(opt: SingleValue<OptionNum>) => {
                          if (!opt) return;
                          const next = ensureTimeWithinBounds({ ...tempParts, minute: opt.value });
                          setTempParts(next);
                        }}
                        isOptionDisabled={(o) => !!o.isDisabled}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t flex items-center justify-between">
              {stage === 'time' ? (
                <button
                  type="button"
                  className="text-sm text-gray-600 hover:bg-gray-100 rounded px-2 py-1 inline-flex items-center gap-1"
                  onClick={() => setStage('date')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5"
                       fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                  </svg>
                  Назад к дате
                </button>
              ) : (
                <span />
              )}

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded"
                  onClick={clear}
                >
                  Очистить
                </button>
                <button
                  type="button"
                  className={`px-3 py-2 text-sm rounded ${validSelection ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}
                  onClick={apply}
                  disabled={!validSelection}
                >
                  Готово
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default DateTimePicker;